import { createContext, useCallback, useEffect, useState } from "react";
import getUser from "./getUserFromLocalStorage";
import {
  clearStoredSession,
  getLastActivity,
  INACTIVITY_TIMEOUT_MS,
  LAST_ACTIVITY_KEY,
} from "./sessionStorage";

export const SessionContext = createContext(null);

const activityEvents = [
  "pointermove",
  "pointerdown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
];

const useSession = () => {
  const [user, setUser] = useState(getUser);

  const logout = useCallback(() => {
    clearStoredSession();
    setUser(null);
  }, []);

  const login = useCallback((authenticatedUser) => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    localStorage.setItem("user", JSON.stringify(authenticatedUser));
    localStorage.setItem("UserName", authenticatedUser.userName);
    setUser(authenticatedUser);
  }, []);

  useEffect(() => {
    let timeoutId;

    const checkSession = () => {
      window.clearTimeout(timeoutId);

      if (!user) {
        return false;
      }

      const lastActivity = getLastActivity();
      const remaining = lastActivity === null
        ? 0
        : INACTIVITY_TIMEOUT_MS - (Date.now() - lastActivity);

      if (!localStorage.getItem("user") || remaining <= 0) {
        logout();
        return false;
      }

      timeoutId = window.setTimeout(checkSession, remaining);
      return true;
    };

    const recordActivity = () => {
      // Check before renewing, including after a suspended browser resumes.
      if (checkSession()) {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      }
    };

    const syncSession = (event) => {
      if (
        event.storageArea !== localStorage ||
        ![null, "user", "UserName", LAST_ACTIVITY_KEY].includes(event.key)
      ) {
        return;
      }

      if (event.key === LAST_ACTIVITY_KEY && user) {
        checkSession();
      } else {
        setUser(getUser());
      }
    };

    const listenerOptions = { passive: true, capture: true };

    if (user) {
      checkSession();
      activityEvents.forEach((event) => {
        window.addEventListener(event, recordActivity, listenerOptions);
      });
      window.addEventListener("focus", checkSession);
      document.addEventListener("visibilitychange", checkSession);
    }

    window.addEventListener("storage", syncSession);

    return () => {
      window.clearTimeout(timeoutId);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, recordActivity, listenerOptions);
      });
      window.removeEventListener("focus", checkSession);
      document.removeEventListener("visibilitychange", checkSession);
      window.removeEventListener("storage", syncSession);
    };
  }, [user, logout]);

  return { user, login, logout };
};

export default useSession;
